import * as React from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';

import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import ContactMailIcon from '@mui/icons-material/ContactMail';
//@ts-ignore
import GetDirectionColor from 'functions/GetDirectionColor';
import { ITicketPostViewModel } from 'Interfaces/ITicketConfig';
import {
  EnumTicketStatus,
  MissingPhoto,
  enumTicketStatusToStringMapping,
} from 'Interfaces/IGlobalTypeConfig';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { MailViewer } from './MailViewer';

const variables = {
  PHOTO_URL: process.env.PUBLIC_URL + '/img/',
};

type props = {
  ticketPost: ITicketPostViewModel;
  handleSelectedPost?: (post: ITicketPostViewModel) => void;
};



export const SingleMessage = ({ ticketPost }: props) => {
  console.log('SingleMessage:', ticketPost);
  const direction =
    ticketPost.direction === 1 || ticketPost.direction === 4
      ? 'row'
      : 'row-reverse';
  const dateTime = dayjs(ticketPost.startTime).format('DD.MM.YYYY HH:mm');

  return (
    <Grid container display={'flex'} flexDirection={direction}>
      <Grid item xs={10.5}>
        <ListItem
          alignItems='flex-start'
          sx={{
            mb: 1,
            borderRadius: 3,
            //backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            //boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.50)',
            borderLeft: 8,
            borderRight: 2,
            borderColor:
              ticketPost.internal === false
                ? GetDirectionColor(ticketPost.direction)
                : GetDirectionColor(6),
            pb: 1,
            ':hover': {
              cursor: 'pointer',
              filter: 'brightness(120%)',
              transition: 'all 0.2s ease-in-out',
            },
          }}
        >
          <ListItemText
            primary={
              <Grid
                container
                sx={{
                  pl: 1,
                  pr: 1,
                  borderRadius: 2,
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    display: 'inline',
                    fontSize: 12,
                    letterSpacing: 2,
                    fontWeight: 'normal',
                  }}
                  component='span'
                  variant='body2'
                  color='text.secondary'
                >
                  {dateTime}
                </Typography>

                <Typography
                  sx={{
                    flexGrow: 1,
                    alignContent: 'center',
                    textAlign: 'center',
                    fontSize: 14,
                  }}
                  color='text.secondary'
                >
                  {ticketPost.internal ? 'INTERNAL ' : ''}
                  {ticketPost.direction > 3 ? 'MERGED' : ''}
                </Typography>

                <Typography
                  sx={{ display: 'inline', fontSize: 14, pr: 1 }}
                  component='span'
                  variant='body1'
                  color='text.secondary'
                >
                  {
                    enumTicketStatusToStringMapping[
                      ticketPost.status as EnumTicketStatus
                    ]
                  }
                </Typography>

                {ticketPost.isEmailSent && (
                  <IconButton
                    aria-label='isMailsendt'
                    size='small'
                    sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                  >
                    <MarkEmailReadRoundedIcon
                      sx={{ fontSize: 18, color: 'success.text' }}
                    />
                  </IconButton>
                )}

                {ticketPost.isCCSent && (
                  <IconButton
                    aria-label='isMailsendt'
                    size='small'
                    sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                  >
                    <ContactMailIcon
                      sx={{ fontSize: 18, color: 'success.text' }}
                    />
                  </IconButton>
                )}

                {ticketPost.isOvertime === true && (
                  <IconButton
                    aria-label='overtime'
                    size='small'
                    sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                  >
                    <MoreTimeRoundedIcon
                      sx={{ fontSize: 18, color: 'success.text' }}
                    />
                  </IconButton>
                )}

                {ticketPost.isBillable && (
                  <IconButton
                    aria-label='isBillable'
                    size='small'
                    sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                  >
                    <AttachMoneyRoundedIcon
                      sx={{ fontSize: 18, color: 'success.text' }}
                    />
                  </IconButton>
                )}

                {ticketPost?.attachments &&
                  ticketPost.attachments.length > 0 && (
                    <IconButton
                      aria-label='attachment'
                      size='small'
                      sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                    >
                      <AttachFileRoundedIcon
                        sx={{ fontSize: 18, color: 'success.text' }}
                      />
                    </IconButton>
                  )}
                {ticketPost?.originalMessageBlobUrl &&
                  ticketPost.originalMessageBlobUrl && (
                    <IconButton
                      aria-label='originalMessage'
                      size='small'
                      sx={{ mt: -0.5, mr: 0.5, p: 0 }}
                      onClick={() => {
                        window.open(
                          ticketPost?.originalMessageBlobUrl || '',
                          '_blank'
                        );
                      }}
                    >
                      <Tooltip
                        title={t('singleMessage.Vieworiginalmessage')}
                        arrow
                      >
                        <MarkEmailReadRoundedIcon
                          sx={{ fontSize: 18, color: 'info.text' }}
                        />
                      </Tooltip>
                    </IconButton>
                  )}
              </Grid>
            }
            secondary={
              <React.Fragment>
                <Typography
                  sx={{
                    mr: 1,
                    display: 'inline',
                    fontWeight: 'bold',
                    fontSize: 16,
                    letterSpacing: 1,
                  }}
                  component='span'
                  variant='body1'
                  color='text.secondary'
                >
                  {ticketPost.author?.name || ''}
                </Typography>

                {/* --------------------------
                    HTML-RENDRING AV MELDINGEN
                   -------------------------- */}
                <MailViewer 
                  html={ticketPost.message} 
                  />

                {/* ChangedBy */}
                {ticketPost.changedBy?.name && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        pl: 1,
                        pr: 1,
                        fontSize: 12,
                        color: 'text.secondary',
                      }}
                      component='span'
                      variant='body2'
                    >
                      {`${t('singleMessage.Changedby')}: ${
                        ticketPost.changedBy.name
                      } at ${dayjs(ticketPost.updated).format(
                        'DD.MM.YYYY HH:mm'
                      )}`}
                    </Typography>

                    <Tooltip
                      title={ticketPost.originalMessage || ''}
                      placement='top-end'
                      arrow
                    >
                      <MarkEmailReadRoundedIcon
                        sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }}
                      />
                    </Tooltip>
                  </Box>
                )}
              </React.Fragment>
            }
          />
        </ListItem>
      </Grid>

      <Grid
        item
        xs={1.5}
        sx={{
          pb: 1,
          width: 100,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={ticketPost.author?.url || MissingPhoto.IMAGE_URL}
          style={{
            borderRadius: '15%',
            width: 64,
            height: 64,
            objectFit: 'cover',
          }}
          alt={'logo'}
        />
      </Grid>
    </Grid>
  );
};
